// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * DSA 1.8 vector layers: from the DSA's `vector` block and its rows to
 * features ready to draw (notes §3–§5, §9, §10).
 *
 * Forward compatibility (§9): a layer whose geometry, time mode or source
 * format this player does not know is skipped, and so is a layer that is
 * schema-valid but breaks the rules of §3–§6 (wrong row length, unclosed
 * ring, unknown style field, missing clock fields) or whose external rows
 * fail their integrity check. Skipping is per layer and reported with a
 * reason; it never fails the dataset.
 */
import { bodyRadiusKmOf, EARTH_RADIUS_KM, kmPerDegree } from '../celestialBodies'
import type { Attrs } from './vectorStyle'
import type {
  DsaClockFields, DsaVectorLayer, VectorColor, VectorField, VectorLayerSpec, VectorNumber, VectorSize, VectorStyle,
} from './vectorTypes'

export type LonLat = [number, number]

export interface VectorFeature {
  /** Row index in the source. */
  index: number
  /** The `id` cell, or the row index. */
  id: string
  /** Every scalar cell by field name (per-vertex cells are in `perVertex`). */
  attrs: Attrs
  point?: LonLat
  /** line: its parts; track: one part. */
  parts?: LonLat[][]
  /** polygon: each polygon's rings, outer first. */
  polygons?: LonLat[][][]
  /** Absolute data times in Unix seconds. */
  time?: number
  start?: number
  /** Infinity while the feature is still ongoing (null `end`). */
  end?: number
  vertexTimes?: number[]
  perVertex: Record<string, unknown[]>
}

export interface CompiledVectorLayer {
  order: number
  name: string
  layerOpacity: number
  supersedes: number[]
  spec: VectorLayerSpec
  /** Unix ms that row times count from. */
  epochMs: number
  features: VectorFeature[]
  /**
   * AYNI (DSA 1.9 §2): km of surface per degree on the DSA's body, for
   * `km` sizes. Absent = the Earth's (111.195); read through {@link kmPerDegOf}.
   */
  kmPerDeg?: number
}

/** km of surface per degree on a compiled layer's body (the Earth's when it does not say). */
export function kmPerDegOf(layer: Pick<CompiledVectorLayer, 'kmPerDeg'>): number {
  return layer.kmPerDeg ?? kmPerDegree(EARTH_RADIUS_KM)
}

export type VectorLayerResult =
  | { ok: true; layer: CompiledVectorLayer }
  | { ok: false; order: number; reason: string }

/** What this player draws (§9 rule 2: anything else is skipped). */
export const SUPPORTED_GEOMETRIES = new Set(['point', 'line', 'polygon', 'track'])
export const SUPPORTED_TIME_MODES = new Set(['instant', 'interval', 'static', 'vertex'])
export const SUPPORTED_ROW_FORMATS = new Set(['dsa-vector-rows-v1'])

/** A `layers[]` entry is a vector layer. */
export function isVectorLayer(layer: unknown): layer is DsaVectorLayer {
  return !!layer && typeof layer === 'object' && (layer as { type?: unknown }).type === 'vector'
    && typeof (layer as { vector?: unknown }).vector === 'object'
}

const REQUIRED_ROLES: Record<string, string[]> = {
  point: ['lon', 'lat'], line: ['coordinates'], polygon: ['coordinates'], track: ['coordinates'],
  instant: ['time'], interval: ['start', 'end'], vertex: ['vertexTimes'], static: [],
}

class RuleError extends Error {}

function fail(reason: string): never {
  throw new RuleError(reason)
}

function isPos(p: unknown): p is LonLat {
  return Array.isArray(p) && p.length >= 2 && typeof p[0] === 'number' && typeof p[1] === 'number'
    && p[0] >= -180 && p[0] <= 180 && p[1] >= -90 && p[1] <= 90
}

function positions(v: unknown, min: number, what: string): LonLat[] {
  if (!Array.isArray(v) || v.length < min || !v.every(isPos)) fail(`${what}: needs ${min}+ [lon, lat] positions in range`)
  return (v as LonLat[]).map((p) => [p[0], p[1]] as LonLat)
}

/** §4: a ring is closed, has 4+ positions and never crosses ±180 (bar the polar edge). */
function ring(v: unknown, what: string): LonLat[] {
  const r = positions(v, 4, what)
  const a = r[0]
  const b = r[r.length - 1]
  if (a[0] !== b[0] || a[1] !== b[1]) fail(`${what}: ring is not closed`)
  for (let i = 1; i < r.length; i++) {
    const d = Math.abs(r[i][0] - r[i - 1][0])
    const polar = d === 360 && r[i][1] === r[i - 1][1] && Math.abs(r[i][1]) === 90
    if (d > 180 && !polar) fail(`${what}: ring crosses the antimeridian (split it, §4)`)
  }
  return r
}

function styleFields(style: VectorStyle): Array<{ field: string; kind: 'number' | 'category' | 'any' }> {
  const out: Array<{ field: string; kind: 'number' | 'category' | 'any' }> = []
  const n = (v: VectorNumber | undefined) => { if (v && typeof v === 'object') out.push({ field: v.field, kind: 'number' }) }
  const s = (v: VectorSize | undefined) => n(v?.value)
  const c = (v: VectorColor | undefined) => {
    if (!v || 'constant' in v) return
    out.push({ field: v.field, kind: v.map === 'category' ? 'category' : 'number' })
  }
  s(style.size); c(style.color); n(style.opacity)
  s(style.stroke?.width); c(style.stroke?.color)
  if (style.zOrder) out.push({ field: style.zOrder.field, kind: 'any' })
  for (const a of [style.enter, style.exit]) { n(a?.seconds); n(a?.delaySeconds) }
  for (const e of style.emphasis ?? []) { out.push({ field: e.when.field, kind: 'any' }); n(e.seconds); c(e.color) }
  s(style.track?.head?.size); c(style.track?.head?.color)
  if (style.label?.when) out.push({ field: style.label.when.field, kind: 'any' })
  return out
}

const NUMERIC = new Set(['number', 'integer'])

function checkSpec(spec: VectorLayerSpec, dsa: DsaClockFields): void {
  if (!SUPPORTED_GEOMETRIES.has(spec.geometry)) fail(`geometry "${spec.geometry}" is not supported here`)
  if (!SUPPORTED_TIME_MODES.has(spec.time?.mode)) fail(`time mode "${spec.time?.mode}" is not supported here`)
  if ((spec.geometry === 'track') !== (spec.time.mode === 'vertex')) fail('track geometry goes with vertex time only')
  const sourceFormat = spec.source?.mode === 'external' ? spec.source.format : 'dsa-vector-rows-v1'
  if (spec.source?.mode !== 'inline' && spec.source?.mode !== 'external') fail('unknown source mode')
  if (!SUPPORTED_ROW_FORMATS.has(sourceFormat)) fail(`row format "${sourceFormat}" is not supported here`)
  const roles = new Map<string, VectorField>()
  for (const f of spec.fields) {
    if (f.role !== 'attribute') {
      if (roles.has(f.role)) fail(`role "${f.role}" appears twice`)
      roles.set(f.role, f)
    }
  }
  for (const r of [...REQUIRED_ROLES[spec.geometry], ...REQUIRED_ROLES[spec.time.mode]]) {
    if (!roles.has(r)) fail(`missing a field with role "${r}"`)
  }
  for (const { field, kind } of styleFields(spec.style ?? {})) {
    const f = spec.fields.find((x) => x.name === field)
    if (!f) fail(`style names unknown field "${field}"`)
    if (kind === 'number' && !NUMERIC.has(f.type)) fail(`style scales non-numeric field "${field}"`)
    if (kind === 'category' && f.type !== 'category') fail(`category colour on non-category field "${field}"`)
  }
  if (spec.time.mode !== 'static') {
    const ok = dsa.timeEnabled !== false && !!dsa.timeRange?.start && (dsa.timeTotalFrames ?? 0) > 0
      && (dsa.videoFrameRate ?? 0) > 0 && (dsa.timeCadenceSeconds ?? 0) > 0
    if (!ok) fail('a timed vector layer needs timeRange, timeTotalFrames, videoFrameRate and timeCadenceSeconds (§6)')
  }
}

function cell(field: VectorField, v: unknown, where: string): unknown {
  if (v === null) {
    if (!field.nullable) fail(`${where}: "${field.name}" is null but not nullable`)
    return null
  }
  switch (field.type) {
    case 'number': if (typeof v !== 'number' || !Number.isFinite(v)) fail(`${where}: "${field.name}" is not a number`); break
    case 'integer': if (!Number.isInteger(v)) fail(`${where}: "${field.name}" is not an integer`); break
    case 'string': if (typeof v !== 'string') fail(`${where}: "${field.name}" is not a string`); break
    case 'boolean': if (typeof v !== 'boolean') fail(`${where}: "${field.name}" is not a boolean`); break
    case 'category':
      if (!field.categories?.some((c) => c.value === v)) fail(`${where}: "${field.name}" = ${JSON.stringify(v)} is not a declared category`)
      break
    default: break
  }
  return v
}

function buildFeature(spec: VectorLayerSpec, row: unknown, index: number, epochS: number): VectorFeature {
  const where = `row ${index}`
  if (!Array.isArray(row) || row.length !== spec.fields.length) fail(`${where}: ${spec.fields.length} cells expected`)
  const f: VectorFeature = { index, id: String(index), attrs: {}, perVertex: {} }
  let vertexCount = 0
  const perVertexLater: Array<[VectorField, unknown]> = []
  spec.fields.forEach((field, i) => {
    const v = row[i]
    switch (field.role) {
      case 'coordinates':
        if (spec.geometry === 'track') {
          f.parts = [positions(v, 2, `${where} track`)]
          vertexCount = f.parts[0].length
        } else if (spec.geometry === 'line') {
          if (!Array.isArray(v) || v.length < 1) fail(`${where}: a line needs at least one part`)
          f.parts = v.map((p, k) => positions(p, 2, `${where} part ${k}`))
          if (f.parts.length === 1) vertexCount = f.parts[0].length
        } else {
          if (!Array.isArray(v) || v.length < 1) fail(`${where}: a polygon cell is a list of polygons`)
          f.polygons = v.map((poly, k) => {
            if (!Array.isArray(poly) || poly.length < 1) fail(`${where} polygon ${k}: no rings`)
            return poly.map((r, j) => ring(r, `${where} polygon ${k} ring ${j}`))
          })
        }
        return
      case 'vertexTimes': {
        if (!Array.isArray(v) || !v.every((t) => typeof t === 'number')) fail(`${where}: vertexTimes must be numbers`)
        for (let k = 1; k < v.length; k++) if (v[k] < v[k - 1]) fail(`${where}: vertexTimes decrease`)
        f.vertexTimes = (v as number[]).map((t) => epochS + t)
        return
      }
      default: break
    }
    if (field.perVertex) { perVertexLater.push([field, v]); return }
    const value = cell(field, v, where)
    f.attrs[field.name] = value
    if (field.role === 'id' && value !== null) f.id = String(value)
    else if (field.role === 'time') { if (value === null) fail(`${where}: time is null`); f.time = epochS + (value as number) }
    else if (field.role === 'start') { if (value === null) fail(`${where}: start is null`); f.start = epochS + (value as number) }
    else if (field.role === 'end') f.end = value === null ? Infinity : epochS + (value as number)
  })
  if (spec.geometry === 'point') {
    const lon = f.attrs[spec.fields.find((x) => x.role === 'lon')!.name]
    const lat = f.attrs[spec.fields.find((x) => x.role === 'lat')!.name]
    if (!isPos([lon, lat])) fail(`${where}: lon/lat out of range`)
    f.point = [lon as number, lat as number]
  }
  if (f.vertexTimes && f.vertexTimes.length !== vertexCount) fail(`${where}: one vertex time per track position`)
  for (const [field, v] of perVertexLater) {
    if (!Array.isArray(v) || v.length !== vertexCount) fail(`${where}: "${field.name}" needs one value per vertex`)
    f.perVertex[field.name] = v.map((x) => cell(field, x, where))
    f.attrs[field.name] = f.perVertex[field.name][0]
  }
  if (f.start !== undefined && f.end !== undefined && f.end < f.start) fail(`${where}: end before start`)
  return f
}

/** Unix ms the layer's row times count from (§5). */
export function vectorEpochMs(spec: VectorLayerSpec, dsa: DsaClockFields): number {
  const ms = Date.parse(spec.time?.epoch ?? dsa.timeRange?.start ?? '')
  return Number.isFinite(ms) ? ms : 0
}

/**
 * Checks a vector layer against §3–§6 and builds its features from the
 * given rows. Never throws: a broken layer comes back as `{ ok: false }`.
 */
export function compileVectorLayer(layer: DsaVectorLayer, dsa: DsaClockFields, rows: unknown[][]): VectorLayerResult {
  try {
    const spec = layer.vector
    checkSpec(spec, dsa)
    const epochMs = vectorEpochMs(spec, dsa)
    if (spec.time.mode !== 'static' && !(spec.time.epoch || dsa.timeRange?.start)) fail('no epoch for row times')
    // DSA 1.9 §2: a km size on a body of unknown radius is never guessed.
    const radiusKm = dsa.bodyRadiusKm === undefined ? EARTH_RADIUS_KM : dsa.bodyRadiusKm
    if (radiusKm === null && /"units":"km"/.test(JSON.stringify(spec.style ?? {}))) fail('km sizes on a body of unknown radius')
    const features = rows.map((row, i) => buildFeature(spec, row, i, epochMs / 1000))
    return {
      ok: true,
      layer: {
        order: layer.order, name: layer.name ?? '', layerOpacity: layer.opacity ?? 1,
        supersedes: Array.isArray(layer.supersedesLayers) ? layer.supersedesLayers.filter(Number.isInteger) : [],
        spec, epochMs, features,
        ...(radiusKm !== null && radiusKm !== EARTH_RADIUS_KM ? { kmPerDeg: kmPerDegree(radiusKm) } : {}),
      },
    }
  } catch (err) {
    return { ok: false, order: layer.order, reason: err instanceof Error ? err.message : String(err) }
  }
}

/** `sha256-<base64>` of the bytes (Web Crypto). */
export async function sha256Integrity(bytes: ArrayBuffer): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))
  let bin = ''
  for (const b of digest) bin += String.fromCharCode(b)
  return `sha256-${btoa(bin)}`
}

export type RowsResult = { ok: true; rows: unknown[][] } | { ok: false; reason: string }

/**
 * The layer's rows (§10.1). An external file resolves against the URL the
 * DSA was fetched from, must match its `integrity`, carry the
 * `dsa-vector-rows-v1` format and list the layer's field names in order.
 */
export async function loadVectorRows(
  spec: VectorLayerSpec, dsaUrl: string | undefined, fetchFn: typeof fetch = fetch,
): Promise<RowsResult> {
  const src = spec.source
  if (src?.mode === 'inline') return Array.isArray(src.rows) ? { ok: true, rows: src.rows } : { ok: false, reason: 'inline rows missing' }
  if (src?.mode !== 'external') return { ok: false, reason: 'unknown source mode' }
  if (!SUPPORTED_ROW_FORMATS.has(src.format)) return { ok: false, reason: `row format "${src.format}" is not supported here` }
  let url: string
  try {
    url = new URL(src.url, dsaUrl).toString()
  } catch {
    return { ok: false, reason: 'rows url does not resolve against the DSA url' }
  }
  try {
    const res = await fetchFn(url)
    if (!res.ok) return { ok: false, reason: `rows fetch failed (${res.status})` }
    const bytes = await res.arrayBuffer()
    if ((await sha256Integrity(bytes)) !== src.integrity) return { ok: false, reason: 'rows fail their integrity check' }
    const doc = JSON.parse(new TextDecoder().decode(bytes)) as { format?: unknown; fields?: unknown; rows?: unknown }
    if (doc.format !== src.format) return { ok: false, reason: 'rows file has another format' }
    const names = spec.fields.map((f) => f.name)
    if (!Array.isArray(doc.fields) || doc.fields.join('\u0000') !== names.join('\u0000')) {
      return { ok: false, reason: 'rows file fields differ from the layer fields' }
    }
    if (!Array.isArray(doc.rows)) return { ok: false, reason: 'rows file has no rows' }
    return { ok: true, rows: doc.rows as unknown[][] }
  } catch (err) {
    return { ok: false, reason: `rows did not load: ${err instanceof Error ? err.message : String(err)}` }
  }
}

/** Loads and compiles one vector layer; a failure is a skipped layer, never a throw. */
export async function prepareVectorLayer(
  layer: DsaVectorLayer, dsa: DsaClockFields, dsaUrl: string | undefined, fetchFn: typeof fetch = fetch,
): Promise<VectorLayerResult> {
  // Rule 2 before any fetch: an unknown geometry/time/format costs nothing.
  const spec = layer.vector
  if (!SUPPORTED_GEOMETRIES.has(spec?.geometry) || !SUPPORTED_TIME_MODES.has(spec?.time?.mode)) {
    return { ok: false, order: layer.order, reason: 'geometry or time mode not supported here' }
  }
  const rows = await loadVectorRows(spec, dsaUrl, fetchFn)
  if (!rows.ok) return { ok: false, order: layer.order, reason: rows.reason }
  return compileVectorLayer(layer, dsa, rows.rows)
}

/** A `.dsa`'s vector layers, with what drawing them needs. */
export interface DsaVectors {
  layers: DsaVectorLayer[]
  clock: DsaClockFields
  /** The other layers by order: what `supersedesLayers` may hide. */
  others: Array<{ order: number; type: string; isLegend: boolean; isPip: boolean }>
}

/**
 * The vector layers of a parsed `.dsa` (any version: XR reads 1.8 and 1.9
 * where it finds them, and nothing older has them), or null when there are none.
 * Lenient like the other DSA readers: odd entries are left out.
 */
export function parseDsaVectors(raw: unknown): DsaVectors | null {
  if (!raw || typeof raw !== 'object') return null
  const doc = raw as Record<string, unknown>
  const layers = Array.isArray(doc.layers) ? doc.layers : []
  const vector = layers.filter(isVectorLayer).filter((l) => Number.isInteger(l.order))
  if (!vector.length) return null
  const others = layers
    .filter((l): l is Record<string, unknown> => !!l && typeof l === 'object' && !isVectorLayer(l))
    .filter((l) => Number.isInteger(l.order))
    .map((l) => ({ order: l.order as number, type: String(l.type ?? ''), isLegend: l.isLegend === true, isPip: l.isPip === true }))
  const timeRange = doc.timeRange && typeof doc.timeRange === 'object' ? doc.timeRange as { start: string; end?: string } : undefined
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : undefined)
  return {
    layers: vector,
    clock: {
      timeEnabled: typeof doc.timeEnabled === 'boolean' ? doc.timeEnabled : undefined,
      timeRange, timeTotalFrames: num(doc.timeTotalFrames), videoFrameRate: num(doc.videoFrameRate), timeCadenceSeconds: num(doc.timeCadenceSeconds),
      // DSA 1.9: the radius of its body (bodyRadiusKm, or celestialBody / body:<name> in the table).
      bodyRadiusKm: bodyRadiusKmOf(doc),
    },
    others,
  }
}
